import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  const refresh = useCallback(async () => {
    if (!user) { setItems([]); return; }
    try {
      const { data } = await api.get("/cart");
      setItems(data.items || []);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const addItem = async (product_id, quantity = 1) => {
    if (!user) { toast.error("Please sign in to add to cart"); return { ok: false }; }
    try {
      await api.post("/cart/items", { product_id, quantity });
      await refresh();
      toast.success("Added to cart");
      return { ok: true };
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not add to cart";
      toast.error(msg);
      return { ok: false, error: msg };
    }
  };

  const updateQty = async (product_id, quantity) => {
    try {
      await api.patch(`/cart/items/${product_id}`, { product_id, quantity });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update quantity");
    }
  };

  const removeItem = async (product_id) => {
    await api.delete(`/cart/items/${product_id}`);
    await refresh();
  };

  const total = items.reduce((s, it) => s + (it.unit_price ?? it.product.price) * it.quantity, 0);
  const count = items.reduce((s, it) => s + it.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, updateQty, removeItem, refresh, total, count }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
